import React, { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { MenuItem, TextField } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';

// One modal for every "Edit …" pencil on the detail page; it owns the draft and hands it to `onSave`.
// field: { key, label, type?: 'text' | 'boolean', required?, disabled?, helperText? }
// `error` shows above the actions; the caller sets it when a save fails and the modal stays open.
const EditFieldsModal = ({ open, title, fields = [], values = {}, saving, error, onClose, onSave }) => {
    const [draft, setDraft] = useState({});

    // Re-seed each time it opens so a cancelled edit never leaks into the next one.
    useEffect(() => {
        if (open) {
            setDraft(
                fields.reduce((acc, field) => {
                    acc[field.key] = values[field.key] ?? '';
                    return acc;
                }, {})
            );
        }
        // Intentionally keyed on `open` only: re-seeding on every `values` change would
        // discard whatever the user is currently typing.
    }, [open]);

    const set = (key) => (e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }));
    const isBlank = (field) => field.required && !String(draft[field.key] ?? '').trim();
    const hasBlankRequired = fields.some(isBlank);

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <ModalHeader
                title={title}
                onClose={onClose}
                css={css`
                    border-bottom: 1px solid lightgray;
                `}
            />
            <ModalContent
                css={css`
                    display: flex;
                    flex-direction: column;
                    gap: 1rem;
                    padding-top: 1.5rem;
                    width: 100%;
                `}
            >
                {fields.map((field) => (
                    <TextField
                        key={field.key}
                        id={`edit-field-${field.key}`}
                        select={field.type === 'boolean'}
                        label={field.label}
                        required={field.required}
                        error={isBlank(field)}
                        value={draft[field.key] ?? ''}
                        onChange={set(field.key)}
                        disabled={field.disabled || saving}
                        helperText={field.helperText}
                        variant="outlined"
                        size="small"
                        fullWidth
                    >
                        {field.type === 'boolean' && [
                            <MenuItem key="yes" value="true">
                                Yes
                            </MenuItem>,
                            <MenuItem key="no" value="false">
                                No
                            </MenuItem>,
                        ]}
                    </TextField>
                ))}
                {error && <Alert severity="error">{error}</Alert>}
            </ModalContent>
            <ModalActions
                css={css`
                    gap: 0.75rem;
                    justify-content: flex-end;
                    padding: 1rem 1.5rem;
                `}
            >
                <SecondaryButton onClick={onClose} disabled={saving}>
                    Cancel
                </SecondaryButton>
                <PrimaryButton loading={saving} disabled={hasBlankRequired} onClick={() => onSave(draft)}>
                    Save
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default EditFieldsModal;
